/**
 * WebSocket Hook - Gestione connessione WebSocket real-time
 *
 * Gestisce:
 * - Connessione automatica con JWT token
 * - Riconnessione automatica con backoff esponenziale
 * - Parsing messaggi JSON
 * - Event listeners tipizzati
 * - Integrazione con React Query per cache invalidation
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { refreshAccessToken } from "@/lib/api-client";
import { storage } from "@/lib/local-storage";
import { toast } from "@/lib/toast-service";

// ==================== Types ====================

export type WebSocketEventType =
    | "document:uploaded"
    | "document:classified"
    | "document:extracted"
    | "document:validation_failed"
    | "expedition:created"
    | "expedition:status_changed"
    | "container:delayed"
    | "container:updated"
    | "container:created"
    | "email:received"
    | "email:sync_completed"
    | "activities:unread_updated"
    | "deadline:approaching"
    | "workflow:manual"
    | "workflow:started"
    | "workflow:in_progress"
    | "workflow:completed"
    | "workflow:failed"
    | "notification:new";

export interface WebSocketMessage<T = Record<string, unknown>> {
    type: "event";
    event: WebSocketEventType;
    timestamp: string;
    data: T;
    metadata: {
        company_id: string;
        source: string;
        event_type?: string;
        user_id?: string;
    };
}

export interface DocumentExtractedData {
    document_id: string;
    document_type: string | null;
    file_name: string | null;
    extraction_status: "extracted" | "failed";
    confidence: number | null;
    error?: string;
}

export interface NotificationData {
    title: string;
    body: string;
    notification_type: "info" | "success" | "warning" | "error";
    action_url?: string;
    document_id?: string;
}

export interface EmailSyncCompletedData {
    account_id: string;
    email_address: string;
    emails_fetched?: number;
    emails_processed: number;
    emails_skipped?: number;
    documents_created: number;
    attention_required?: number;
    success: boolean;
    error?: string;
}

export type WebSocketStatus = "connecting" | "connected" | "disconnected" | "error";

interface UseWebSocketOptions {
    /** Auto-connect on mount */
    autoConnect?: boolean;
    /** Show toast notifications for document events */
    showToasts?: boolean;
    /** Reconnect on disconnect */
    autoReconnect?: boolean;
    /** Max reconnect attempts (0 = infinite) */
    maxReconnectAttempts?: number;
    /** Initial reconnect delay in ms */
    reconnectDelay?: number;
}

// ==================== Hook ====================

export function useWebSocket(options: UseWebSocketOptions = {}) {
    const { autoConnect = true, showToasts = true, autoReconnect = true, maxReconnectAttempts = 10, reconnectDelay = 1000 } = options;

    const queryClient = useQueryClient();
    const wsRef = useRef<WebSocket | null>(null);
    const reconnectAttemptRef = useRef(0);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [status, setStatus] = useState<WebSocketStatus>("disconnected");
    const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);

    // Build WebSocket URL
    const getWebSocketUrl = useCallback(() => {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";
        // Rimuovi trailing slash se presente per evitare doppi slash
        const cleanBaseUrl = baseUrl.replace(/\/$/, "");
        const wsProtocol = cleanBaseUrl.startsWith("https") ? "wss" : "ws";
        const wsUrl = cleanBaseUrl.replace(/^https?/, wsProtocol);
        const token = storage.getItem("access_token");

        if (!token) {
            console.warn("[WebSocket] No access token available");
            return null;
        }

        return `${wsUrl}/ws?token=${token}`;
    }, []);

    // Handle incoming messages
    const handleMessage = useCallback(
        (event: MessageEvent) => {
            try {
                const message = JSON.parse(event.data) as WebSocketMessage;
                setLastMessage(message);

                console.log("[WebSocket] Message received:", message.event, message.data);

                // Handle specific event types
                switch (message.event) {
                    case "document:extracted": {
                        const data = message.data as unknown as DocumentExtractedData;

                        // Invalidate document queries to refresh UI
                        queryClient.invalidateQueries({ queryKey: ["documents"] });
                        queryClient.invalidateQueries({ queryKey: ["document", data.document_id] });
                        queryClient.invalidateQueries({ queryKey: ["folders"] });

                        // If this was an arrangement extraction, invalidate related queries
                        if (data.document_type === "document_arrangement") {
                            queryClient.invalidateQueries({ queryKey: ["document-arrangements"] });
                            queryClient.invalidateQueries({ queryKey: ["business-partners"] });
                            queryClient.invalidateQueries({ queryKey: ["business-partner"] });
                            queryClient.invalidateQueries({ queryKey: ["companies"] });
                            queryClient.invalidateQueries({ queryKey: ["contacts"] });
                        }

                        // Show success toast
                        if (showToasts && data.extraction_status === "extracted") {
                            if (data.document_type === "document_arrangement") {
                                toast.success("Receiver profile updated with arrangement data");
                            } else {
                                toast.success(`Documento "${data.file_name || "documento"}" elaborato con successo`);
                            }
                        }
                        break;
                    }

                    case "document:classified": {
                        // Invalidate document queries
                        queryClient.invalidateQueries({ queryKey: ["documents"] });
                        break;
                    }

                    case "document:validation_failed": {
                        // Invalidate document queries
                        queryClient.invalidateQueries({ queryKey: ["documents"] });

                        if (showToasts) {
                            toast.warning("Validazione documento fallita");
                        }
                        break;
                    }

                    case "notification:new": {
                        const data = message.data as unknown as NotificationData;

                        // Show notification based on type
                        if (showToasts) {
                            switch (data.notification_type) {
                                case "error":
                                    toast.error(data.body, { description: data.title });
                                    break;
                                case "warning":
                                    toast.warning(data.body);
                                    break;
                                case "success":
                                    toast.success(data.body);
                                    break;
                                default:
                                    toast.info(data.body);
                            }
                        }
                        break;
                    }

                    case "expedition:status_changed":
                    case "expedition:created": {
                        // Invalidate expedition/container queries
                        queryClient.invalidateQueries({ queryKey: ["containers"] });
                        queryClient.invalidateQueries({ queryKey: ["expeditions"] });
                        break;
                    }

                    case "container:updated":
                    case "container:created": {
                        // Invalidate container recap queries for real-time updates
                        queryClient.invalidateQueries({ queryKey: ["containers-recap"] });
                        queryClient.invalidateQueries({ queryKey: ["containers"] });
                        queryClient.invalidateQueries({ queryKey: ["expeditions"] });

                        if (showToasts) {
                            const action = message.event === "container:created" ? "creato" : "aggiornato";
                            toast.info(`Container ${action}`);
                        }
                        break;
                    }

                    case "workflow:completed":
                    case "workflow:failed": {
                        // Invalidate workflow queries
                        queryClient.invalidateQueries({ queryKey: ["workflows"] });
                        break;
                    }

                    case "email:received": {
                        // Invalidate email queries
                        queryClient.invalidateQueries({ queryKey: ["emails"] });
                        queryClient.invalidateQueries({ queryKey: ["activities"] });
                        break;
                    }

                    case "email:sync_completed": {
                        const data = message.data as unknown as EmailSyncCompletedData;

                        // Invalidate email-related queries
                        queryClient.invalidateQueries({ queryKey: ["email-accounts"] });
                        queryClient.invalidateQueries({ queryKey: ["emails"] });
                        queryClient.invalidateQueries({ queryKey: ["activities"] });
                        queryClient.invalidateQueries({ queryKey: ["email", "counts"] });

                        if (showToasts) {
                            if (data.success) {
                                toast.success(
                                    `Sync completata: ${data.emails_processed} email, ${data.documents_created} documenti`,
                                    { description: data.email_address }
                                );
                            } else {
                                const errorMsg = data.error || "Errore sconosciuto";
                                const isAuthError = errorMsg.toLowerCase().includes("invalid_grant") || 
                                                   errorMsg.toLowerCase().includes("token has been expired") ||
                                                   errorMsg.toLowerCase().includes("revoked") ||
                                                   errorMsg.toLowerCase().includes("authentication");
                                
                                toast.error(
                                    isAuthError 
                                        ? `Autenticazione richiesta per ${data.email_address}`
                                        : `Sync fallita: ${errorMsg}`,
                                    { 
                                        description: isAuthError 
                                            ? "Il token di accesso è scaduto. Clicca sull'icona di warning per riautenticare."
                                            : data.email_address,
                                        duration: isAuthError ? 8000 : 5000
                                    }
                                );
                            }
                        }
                        break;
                    }

                    case "activities:unread_updated": {
                        queryClient.invalidateQueries({ queryKey: ["activities"] });
                        break;
                    }

                    default:
                        // Unknown event type, log it
                        console.log("[WebSocket] Unhandled event:", message.event);
                }
            } catch (error) {
                console.error("[WebSocket] Error parsing message:", error);
            }
        },
        [queryClient, showToasts],
    );

    // Connect to WebSocket
    const connect = useCallback(() => {
        const url = getWebSocketUrl();
        if (!url) {
            setStatus("error");
            return;
        }

        // Close existing connection
        if (wsRef.current) {
            wsRef.current.close();
        }

        setStatus("connecting");
        console.log("[WebSocket] Connecting...");

        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
            console.log("[WebSocket] Connected");
            setStatus("connected");
            reconnectAttemptRef.current = 0;
        };

        ws.onmessage = handleMessage;

        ws.onerror = (error) => {
            console.error("[WebSocket] Error:", error);
            setStatus("error");
        };

        ws.onclose = (event) => {
            console.log("[WebSocket] Disconnected:", event.code, event.reason);
            setStatus("disconnected");
            wsRef.current = null;

            // Don't reconnect on intentional close
            if (event.code === 1000) return;

            // Auto-reconnect logic
            if (autoReconnect && (maxReconnectAttempts === 0 || reconnectAttemptRef.current < maxReconnectAttempts)) {
                const delay = Math.min(
                    reconnectDelay * Math.pow(2, reconnectAttemptRef.current),
                    30000, // Max 30 seconds
                );

                // If server rejected due to expired token (4001), refresh first
                if (event.code === 4001) {
                    console.log("[WebSocket] Token expired, refreshing before reconnect...");
                    reconnectTimeoutRef.current = setTimeout(() => {
                        reconnectAttemptRef.current++;
                        refreshAccessToken()
                            .then(() => {
                                console.log("[WebSocket] Token refreshed, reconnecting...");
                                connect();
                            })
                            .catch(() => {
                                console.warn("[WebSocket] Token refresh failed, stopping reconnect");
                                // Token refresh failed — user will be logged out by api-client
                            });
                    }, delay);
                } else {
                    console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttemptRef.current + 1})`);
                    reconnectTimeoutRef.current = setTimeout(() => {
                        reconnectAttemptRef.current++;
                        connect();
                    }, delay);
                }
            }
        };
    }, [getWebSocketUrl, handleMessage, autoReconnect, maxReconnectAttempts, reconnectDelay]);

    // Disconnect from WebSocket
    const disconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (wsRef.current) {
            wsRef.current.close(1000, "User disconnect");
            wsRef.current = null;
        }

        setStatus("disconnected");
    }, []);

    // Send message (if needed in future)
    const sendMessage = useCallback((message: unknown) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(message));
        } else {
            console.warn("[WebSocket] Cannot send message: not connected");
        }
    }, []);

    // Auto-connect on mount
    useEffect(() => {
        if (autoConnect) {
            // Small delay to ensure token is available
            const timeout = setTimeout(connect, 100);
            return () => clearTimeout(timeout);
        }
    }, [autoConnect, connect]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            disconnect();
        };
    }, [disconnect]);

    return {
        status,
        lastMessage,
        connect,
        disconnect,
        sendMessage,
        isConnected: status === "connected",
    };
}
